import { defineStore } from 'pinia'
import httpClient from '@/api/httpClient'
import store from '@/store'

export const useUserStore = defineStore('user', {
  state: () => ({
    user: {
      id: null,
      first_name: '',
      last_name: '',
      name: null,
      email: '',
      enabled: true,
      role_ids: [],
      role_names: [],
      byline: '',
      ext: '',
      remote_phone: '',
      password: '',
      password_confirmation: '',
      password_choice: 'email',
      set_password_token: false
    },
  }),

  actions: {
    async fetch({ userId }) {
      this.fetchInitiated = true

      try {
        const { data: { user }} = await httpClient.get(`/api/internal/users/${userId}`)
        this.user = user
      } catch (err) {
        store.dispatch('messages/addError', err, { root: true })
      }
    },

    async create() {
      const {
        user: {
          first_name,
          last_name,
          email,
          byline,
          ext,
          remote_phone,
          role_ids,
          password_choice
        }
      } = this

      let { user: { password, password_confirmation } } = this

      if(password_choice !== 'now') {
        // Clear out password fields if not setting password now
        password = null
        password_confirmation = null
      }

      try {
        const { data: { user, message }} = await httpClient.post('/api/internal/users', {
          user: {
            first_name,
            last_name,
            email,
            byline,
            ext,
            remote_phone,
            role_ids,
            password,
            password_confirmation,
          }
        })
        this.user = user
        if (message) {
          store.dispatch('messages/addNotice', message, { root: true })
        }
        return user
      } catch (err) {
        store.dispatch('messages/addError', err, { root: true })
        throw err
      }
    },

    async update({ userId }) {
      const {
        user: {
          first_name,
          last_name,
          email,
          byline,
          ext,
          remote_phone,
          enabled,
          role_ids,
          password,
          password_confirmation
        }
      } = this

      // If password fields are blank, exclude them so that an empty password field in the UI
      // is not treated as an attempt to set a blank password
      const passwordFields = Object.fromEntries(
        Object.entries({ password, password_confirmation }).filter(([, value]) => value && value.length > 0)
      )

      try {
        const { data: { user } } = await httpClient.patch(`/api/internal/users/${userId}`, {
          user: {
            first_name,
            last_name,
            email,
            byline,
            ext,
            remote_phone,
            enabled,
            role_ids,
            ...passwordFields
          }
        })

        this.user = user
        store.dispatch('messages/addNotice', 'User updated successfully.', { root: true })
        return user
      } catch (err) {
        store.dispatch('messages/addError', err, { root: true })
        throw err
      }
    },

    async destroy({ userId }) {
      await httpClient.delete(`/api/internal/users/${userId}`)
      this.$reset()

      store.dispatch('messages/addNotice', 'User deleted successfully.', { root: true })
    }
  }
})
